import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Send } from 'lucide-react';
import { authAxios } from '@/lib/axios';
import { useToast } from '@/hooks/use-toast';
import {
  parseConnections,
  type BotChannel,
  type ChannelConnection,
} from '@/lib/botConnections';

export const BotConnectionSettings = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  // null = still loading; [] = known-none; otherwise one entry per connected channel
  const [connections, setConnections] = useState<ChannelConnection[] | null>(null);
  // channel awaiting confirmation in the dialog, or null when closed
  const [confirmChannel, setConfirmChannel] = useState<BotChannel | null>(null);
  // channel whose disconnect request is in flight
  const [disconnecting, setDisconnecting] = useState<BotChannel | null>(null);

  useEffect(() => {
    let active = true;
    authAxios
      .get('/api/v1/link/connection')
      .then((res) => {
        if (active) setConnections(parseConnections(res.data));
      })
      .catch(() => {
        // Fail closed — never show a stale "connected" card.
        if (active) setConnections([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const channelLabel = (channel: BotChannel) =>
    t(`botConnection.channelName.${channel}`);

  const handleDisconnect = async (channel: BotChannel) => {
    setDisconnecting(channel);
    try {
      await authAxios.post('/api/v1/link/disconnect', { channel });
      toast({
        title: t('botConnection.disconnectedTitle'),
        description: t('botConnection.disconnectedDesc', { channel: channelLabel(channel) }),
      });
      setConfirmChannel(null);
      setConnections((prev) => (prev ?? []).filter((c) => c.channel !== channel));
    } catch {
      toast({
        title: t('botConnection.errorTitle'),
        description: t('botConnection.errorDesc'),
        variant: 'destructive',
      });
    } finally {
      setDisconnecting(null);
    }
  };

  // Hidden while still loading and whenever no channel is connected.
  if (connections === null || connections.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Send className="h-5 w-5" />
          {t('botConnection.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('botConnection.description')}</p>
        {connections.map((c) => (
          <div
            key={c.channel}
            className="flex items-center justify-between gap-4 border-t pt-4 first:border-t-0 first:pt-0"
          >
            <div>
              <p className="font-medium">{channelLabel(c.channel)}</p>
              {c.linkedAt && (
                <p className="text-xs text-muted-foreground">
                  {t('botConnection.connectedSince', {
                    date: new Date(c.linkedAt).toLocaleDateString(),
                  })}
                </p>
              )}
            </div>
            <Button variant="destructive" onClick={() => setConfirmChannel(c.channel)}>
              {t('botConnection.disconnectButton', { channel: channelLabel(c.channel) })}
            </Button>
          </div>
        ))}
      </CardContent>

      <AlertDialog
        open={confirmChannel !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmChannel(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmChannel &&
                t('botConnection.confirmTitle', { channel: channelLabel(confirmChannel) })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('botConnection.confirmBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting !== null}>
              {t('botConnection.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (confirmChannel) handleDisconnect(confirmChannel);
              }}
              disabled={disconnecting !== null}
            >
              {disconnecting !== null ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('botConnection.confirmDisconnect')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
